import { Col, Form, Modal, ModalProps, Row } from 'antd';

import { useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  AppPageHeader,
  FloatingFormItem,
  InfiniteTable,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  AccountPayableColumnConfig,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { InstallmentSituation } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import {
  useInstallmentsList,
  useAccountPayableReverseMultipleInstallment,
  useAccountPayableDeleteMultipleInstallment,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { InstallmentList } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import { AccountPayableFieldOption } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import moment from 'moment';
import { SorterResult } from 'antd/lib/table/interface';
import { DateTimeFormat } from '@/utils/moment';
import AccountPayableListExtraFilters from './AccountPayableListExtraFilters';

interface SelectedAccountPayablesProps extends Omit<ModalProps, 'onCancel'> {
  selectedInstallments?: { installmentId: number[]; planAccountId: number };
  onCancel: () => void;
  btnType?: 'reverse' | 'delete';
}

export const AccountPayableReverseMultipleInstallmentsModal: React.FC<
  SelectedAccountPayablesProps
> = ({ btnType, open, onCancel, selectedInstallments }) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();
  const [form] = Form.useForm();

  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    order: 'DESC',
    situation: InstallmentSituation.settled,
  });

  const [selectedInstallmentsKeys, setSelectedInstallmentsKeys] = useState<
    Key[]
  >([]);

  const reverseMutation = useAccountPayableReverseMultipleInstallment();
  const deleteMutation = useAccountPayableDeleteMultipleInstallment();

  const { data, isFetching, hasNextPage, fetchNextPage } = useInstallmentsList(
    {
      ...queryParams,
      situation:
        btnType === 'delete'
          ? InstallmentSituation.open
          : InstallmentSituation.settled,
    },
    open,
  );

  const accountPayables = flatInfiniteData(data);

  function handleOrderAccountPayable(sorter: SorterResult<InstallmentList>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderColumns === AccountPayableColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: AccountPayableColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  useEffect(() => {
    if (open && selectedInstallments) {
      setSelectedInstallmentsKeys({ ...selectedInstallments?.installmentId });
    }
  }, [open]);

  const handleOk = async () => {
    const values = await form.validateFields();

    if (btnType === 'delete') {
      deleteMutation.mutateAsync({
        installmentIds: selectedInstallmentsKeys,
      });
    }

    if (btnType === 'reverse') {
      reverseMutation.mutateAsync({
        installmentPayableId: selectedInstallmentsKeys,
        planAccountId: values.planAccountId,
        reversalDate: moment().format(DateTimeFormat),
      });
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams({
      order: 'DESC',
      situation: InstallmentSituation.settled,
    });

    setSelectedInstallmentsKeys([]);

    onCancel();
  };

  const columns: ColumnsType<InstallmentList> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.code' }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.code,
      sorter: true,
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      width: 220,
      key: AccountPayableFieldOption.company,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: AccountPayableFieldOption.description,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.emissionDate,
      sorter: true,
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.dueDate,
      sorter: true,
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 110,
      key: AccountPayableFieldOption.payDate,
      sorter: true,
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue +
            (record.discountIncrease ?? 0) -
            (record.credit ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.value,
      sorter: true,
    },
  ];

  const handleInstallmentSelect = (selectedRowKeys: Key[]) => {
    setSelectedInstallmentsKeys(selectedRowKeys);
  };

  return (
    <Modal
      destroyOnClose
      title={`${
        btnType === 'delete' ? 'Exclusão' : 'Estorno'
      } de múltiplas parcelas`}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      width={1400}
      okText={btnType === 'delete' ? 'Excluir' : 'Estornar'}
      okButtonProps={{ disabled: !selectedInstallmentsKeys.length }}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            {btnType === 'reverse' && (
              <div style={{ marginBottom: '24px' }}>
                <Form form={form} preserve={false}>
                  <FloatingFormItem
                    label="Selecione o Plano de Contas"
                    name="planAccountId"
                    rules={[defaultEmptyFieldError]}
                  >
                    <PlanAccountsSelect
                      allowClear
                      queryFilter={{
                        active: true,
                        hierarchy: PlanAccountHierarchy.Analitical,
                        destination: PlanAccountDestination.Revenue,
                      }}
                    />
                  </FloatingFormItem>
                </Form>
              </div>
            )}

            <AppPageHeader
              onSearch={(value) =>
                setQueryParams((prev) => ({ ...prev, search: value }))
              }
              searchValue={queryParams.search}
              extraFilters={
                <AccountPayableListExtraFilters
                  setQueryParams={setQueryParams}
                  queryParams={queryParams}
                />
              }
            />

            <InfiniteTable<InstallmentList>
              columns={columns}
              dataSource={accountPayables}
              loading={isFetching}
              rowKey={(row) => row.id}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              rowSelection={{
                selectedRowKeys: selectedInstallmentsKeys,
                onChange: handleInstallmentSelect,
              }}
              onChange={(_p, _f, sorter) => {
                if (!Array.isArray(sorter)) {
                  handleOrderAccountPayable(sorter);
                }
              }}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};
